import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import { HiPlus, HiOutlineInformationCircle } from 'react-icons/hi';
import AppLoadingState from './common/AppLoadingState';
import { AppPageShell, Button, PageHeader } from './common/ui';

const CustomTools = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    speech: '',
    url: '',
    method: 'GET',
    headers: {},
    body: {},
    query: {},
    input_schema: {},
    response: {},
    timeout: 10000
  });

  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await api.getCustomTools();
      setTools(response.tools || []);
    } catch (error) {
      toast.error('Failed to fetch custom tools');
      console.error('Error fetching tools:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  const handleCreateTool = async () => {
    try {
      await api.createCustomTool(formData);
      toast.success('Custom tool created successfully');
      setIsCreateModalOpen(false);
      resetForm();
      fetchTools();
    } catch (error) {
      toast.error(error.message || 'Failed to create custom tool');
    }
  };

  const handleUpdateTool = async () => {
    try {
      await api.updateCustomTool(selectedTool.tool_id, formData);
      toast.success('Custom tool updated successfully');
      setIsEditModalOpen(false);
      setSelectedTool(null);
      resetForm();
      fetchTools();
    } catch (error) {
      toast.error(error.message || 'Failed to update custom tool');
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (window.confirm('Are you sure you want to delete this tool?')) {
      try {
        await api.deleteCustomTool(toolId);
        toast.success('Custom tool deleted successfully');
        fetchTools();
      } catch (error) {
        toast.error(error.message || 'Failed to delete custom tool');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      speech: '',
      url: '',
      method: 'GET',
      headers: {},
      body: {},
      query: {},
      input_schema: {},
      response: {},
      timeout: 10000
    });
  };

  const handleEditClick = (tool) => {
    setSelectedTool(tool);
    setFormData({
      name: tool.tool.name,
      description: tool.tool.description,
      speech: tool.tool.speech || '',
      url: tool.tool.url,
      method: tool.tool.method || 'GET',
      headers: tool.tool.headers || {},
      body: tool.tool.body || {},
      query: tool.tool.query || {},
      input_schema: tool.tool.input_schema || {},
      response: tool.tool.response || {},
      timeout: tool.tool.timeout || 10000
    });
    setIsEditModalOpen(true);
  };

  const ToolModal = ({ isOpen, onClose, isEdit }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {isEdit ? 'Edit Custom Tool' : 'Create New Custom Tool'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Speech</label>
                  <input
                    type="text"
                    value={formData.speech}
                    onChange={(e) => setFormData({ ...formData, speech: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">URL</label>
                  <input
                    type="text"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Method</label>
                  <select
                    value={formData.method}
                    onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Headers (JSON)</label>
                  <textarea
                    value={JSON.stringify(formData.headers, null, 2)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, headers: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 font-mono"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Body (JSON)</label>
                  <textarea
                    value={JSON.stringify(formData.body, null, 2)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, body: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 font-mono"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Query Parameters (JSON)</label>
                  <textarea
                    value={JSON.stringify(formData.query, null, 2)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, query: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 font-mono"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Input Schema (JSON)</label>
                  <textarea
                    value={JSON.stringify(formData.input_schema, null, 2)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, input_schema: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 font-mono"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Response Schema (JSON)</label>
                  <textarea
                    value={JSON.stringify(formData.response, null, 2)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, response: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 font-mono"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Timeout (ms)</label>
                  <input
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  />
                </div>
              </div>
              <div className="mt-6 sm:mt-8 sm:grid sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={isEdit ? handleUpdateTool : handleCreateTool}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                >
                  {isEdit ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 sm:mt-0 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppPageShell maxWidthClass="max-w-7xl">
      <PageHeader
        eyebrow="Integrations"
        title="Custom tools"
        description="Define HTTP tools your agents can call mid-conversation to fetch data or trigger workflows."
        actions={
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <HiPlus className="-ml-1 mr-2 h-5 w-5" />
            Create tool
          </Button>
        }
      />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <HiOutlineInformationCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Custom Tools allow you to integrate external APIs with your AI agents. Create tools that can be used during conversations to fetch data or perform actions.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <AppLoadingState layout="compact" showLabel={false} />
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {tools.map((tool) => (
              <li key={tool.tool_id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {tool.tool.name}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {tool.tool.description}
                      </p>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <span className="truncate">
                          {tool.tool.url}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleEditClick(tool)}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTool(tool.tool_id)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
            {tools.length === 0 && (
              <li>
                <div className="px-4 py-8 text-center text-gray-500">
                  No custom tools found. Create one to get started!
                </div>
              </li>
            )}
          </ul>
        </div>
      )}

      <ToolModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetForm();
        }}
        isEdit={false}
      />

      <ToolModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTool(null);
          resetForm();
        }}
        isEdit={true}
      />
    </AppPageShell>
  );
};

export default CustomTools; 